<template>
  <div class="image-gallery-popup" :style="popupStyle">
    <div class="image-gallery-popup-image">
      <img
        :src="closeButton"
        alt="closeButton"
        class="close-button"
        @click="closePopup"
      />
      <div v-if="props.popupImage?.thumbnailUrl" class="popup-image-wrapper">
        <img
          ref="popupImgRef"
          :src="props.popupImage.thumbnailUrl"
          alt="bestImage"
          @load="onPopupImageLoad"
        />
        <!-- Highlight point overlay -->
        <img
          v-if="props.popupImage?.coordinatesOnImage"
          src="@/assets/icons/pinpoint.svg"
          alt="highlight point"
          class="highlight-point"
          :style="highlightStyle"
        />
      </div>
      <div v-else class="loading-container">
        <q-spinner :color="theme.gree300" size="60px" />
      </div>
    </div>
    <div class="full-screen-button" @click="fullScreen">
      {{ $t("imageNavigation.fullScreen") }}
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";

import closeButton from "@/assets/icons/close-popup.svg";
import { useTheme } from "@/composables/useTheme.js";
const { theme } = useTheme();

const props = defineProps([
  "popupImage",
  "toggleFullScreenImageActive",
  "mousePositionOnScreen",
  "handleImageSelected",
  "originalImageWidth",
  "originalImageHeight",
]);

const popupImgRef = ref(null);
const highlightStyle = ref({ visibility: "hidden" });

const scalePointToThumbnail = function (image, point) {
  const thumbnailWidth = image.naturalWidth;
  const thumbnailHeight = image.naturalHeight;

  const scaleToThumbnailX = thumbnailWidth / props.originalImageWidth;
  const scaleToThumbnailY = thumbnailHeight / props.originalImageHeight;

  return {
    x: point.x * scaleToThumbnailX,
    y: point.y * scaleToThumbnailY,
  };
};

const scaleThumbnailToDisplayed = function (image, pointThumbnail) {
  const thumbnailWidth = image.naturalWidth;
  const thumbnailHeight = image.naturalHeight;

  const containerWidth = image.clientWidth;
  const containerHeight = image.clientHeight;

  const thumbnailAspect = thumbnailWidth / thumbnailHeight;
  const containerAspect = containerWidth / containerHeight;

  let displayedWidth, displayedHeight, offsetX, offsetY;

  if (containerAspect > thumbnailAspect) {
    displayedHeight = containerHeight;
    displayedWidth = displayedHeight * thumbnailAspect;
    offsetX = (containerWidth - displayedWidth) / 2;
    offsetY = 0;
  } else {
    displayedWidth = containerWidth;
    displayedHeight = displayedWidth / thumbnailAspect;
    offsetX = 0;
    offsetY = (containerHeight - displayedHeight) / 2;
  }

  const scaleX = displayedWidth / thumbnailWidth;
  const scaleY = displayedHeight / thumbnailHeight;

  return {
    x: pointThumbnail.x * scaleX + offsetX,
    y: pointThumbnail.y * scaleY + offsetY,
  };
};

const getHighlightPointStyle = (image, imageElement) => {
  if (!imageElement || !image.coordinatesOnImage || imageElement.clientWidth === 0) {
    return { visibility: "hidden" };
  }

  const originalPoint = {
    x: image.coordinatesOnImage.x * props.originalImageWidth,
    y: image.coordinatesOnImage.y * props.originalImageHeight,
  };

  const thumbPoint = scalePointToThumbnail(imageElement, originalPoint);
  const pointDisplayed = scaleThumbnailToDisplayed(imageElement, thumbPoint);

  return {
    visibility: "visible",
    left: `${pointDisplayed.x}px`,
    top: `${pointDisplayed.y}px`,
    transform: "translate(-50%, -50%)",
  };
};

const updateHighlightStyle = () => {
  const imgEl = popupImgRef.value;
  if (
    props.popupImage &&
    imgEl &&
    imgEl.naturalWidth > 0 &&
    imgEl.clientWidth > 0 &&
    props.popupImage.coordinatesOnImage
  ) {
    highlightStyle.value = getHighlightPointStyle(props.popupImage, imgEl);
  } else {
    highlightStyle.value = { visibility: "hidden" };
  }
};

const onPopupImageLoad = () => {
  updateHighlightStyle();
};

watch(
  () => [props.popupImage, props.popupImage?.coordinatesOnImage],
  () => {
    highlightStyle.value = { visibility: "hidden" };
    updateHighlightStyle();
  }
);

onMounted(() => {
  updateHighlightStyle();
});

const emit = defineEmits(["close"]);

// 486 is the width of the popup combined with the right sidebar
// 186 is width of popup and we are trying to align popup away from right sidebar
// 230 is the height of the popup and we are trying to align popup away from top of the screen
const popupStyle = computed(() => ({
  top: `${
    props.mousePositionOnScreen.y - 230 < 0 ? 0 : props.mousePositionOnScreen.y - 230
  }px`,
  left: `${
    props.mousePositionOnScreen.x + 20 > window.innerWidth - 486
      ? props.mousePositionOnScreen.x - 186
      : props.mousePositionOnScreen.x + 20
  }px`,
}));

function fullScreen() {
  props.handleImageSelected(props.popupImage, true);
  props.toggleFullScreenImageActive(true);
}

function closePopup() {
  emit("close");
}
</script>

<style scoped lang="scss">
.image-gallery-popup {
  width: 181px;
  height: 180px;
  background: linear-gradient(to bottom, rgba(52, 52, 52, 0.5), rgba(137, 136, 136, 0.5));
  backdrop-filter: blur(15.9px);
  position: fixed;
  z-index: 1000;
  border-radius: 10px;
  border: 1px solid linear-gradient(to bottom, #343434, #898888); // Updated border to match gradient
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .image-gallery-popup-image {
    width: 100%;
    height: 100%;
    img {
      width: 160px;
      height: 120px;
      border-radius: 10px;
    }
    .close-button {
      position: absolute;
      top: -5px;
      right: -5px;
      cursor: pointer;
      width: 24px;
      height: 24px;
    }
    .popup-image-wrapper {
      position: relative;
      display: inline-block;
    }
    .highlight-point {
      position: absolute;
      width: 10px;
      height: 10px;
      transform: translate(-50%, -50%);
      pointer-events: none;
    }
  }

  .full-screen-button {
    width: 100%;
    height: 33px;
    background: var(--color-gree300);
    border-radius: 10px;
    color: var(--color-black);
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    padding: 5px 0;
    cursor: pointer;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    height: 100%;
  }
}
</style>
